<template>
    <span class="label-wrapper">
        <label :for="forControl"
               :class="`form__label form__label--${this.forDef} --${this.forDef} js-label-${this.forDef}`"
               :tabindex="tabIndex"
               @click="onClick"
               @keyup.enter="onEnter"
               @keyup.space="onSpace"><slot /></label>
    </span>
</template>

<script>

import _ from 'lodash'

import KeyQuery from '../../../helpers/keyquery'

/**
 * An HTML label control. Where a label wraps other content use hh-label-wrapping.vue instead.
 */
export default {

    props: {
        forDef: {
            type: String,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        tabstop: {
            type: Boolean,
            default: false,
        },
        tabindex: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            clickStarted: false,
            keyupEnterStarted: false,
            keyupSpaceStarted: false,
        }
    },

    computed: {

        /**
         * @returns {?string}
         */
        forControl() {
            return this.wrapsControl ? null : this.forDef
        },

        /**
         * @returns {number}
         */
        tabIndex() {
            return this.tabindex || this.tabstop ? 0 : -1
        },

        /**
         * @returns {boolean}
         */
        wrapsControl() {
            return this.$scopedSlots.default !== undefined && this.$scopedSlots.default !== null
        },
    },

    methods: {

        /**
         * Relocates the event to the linked control (if there is one).
         * @param {string} eventName The name of the event.
         * @param {Object} $event The event object.
         */
        _relocateEvent(eventName, $event) {
            const mappedControl = document && document.getElementById(this.forDef)
            if (!$event.abortInLabel && mappedControl) {
                let mappedEvent = null
                if (eventName.includes('key')) {
                    const elements = eventName.split('.')
                    const eventData = KeyQuery.queryEventData(elements[1])
                    eventData.bubbles = false
                    mappedEvent = new KeyboardEvent(elements[0], eventData)
                } else {
                    mappedEvent = new MouseEvent(eventName, {
                        view: window,
                        bubbles: false,
                        cancelable: true,
                    })
                }
                mappedControl.checked = mappedControl.checked !== undefined ? !mappedControl.checked : undefined
                // noinspection JSCheckFunctionSignatures
                mappedControl.dispatchEvent(mappedEvent)
            } else {
                this.$emit(eventName, $event)
            }

            _.delay(this._resetStackState, 100, this)
        },

        /**
         * Resets the stack state to prevent overflows.
         * @param {this} component The Vue component (this)
         */
        _resetStackState(component) {
            component.clickStarted = false
            component.keyupEnterStarted = false
            component.keyupSpaceStarted = false
        },

        /**
         * Event that fires on clicking the control.
         * This will trigger the click to the linked control UNLESS the linked control is wrapped.
         * @param {Object} $event The event data.
         */
        onClick($event) {
            if (!this.clickStarted) {
                this.clickStarted = true

                if (!this.wrapsControl)
                    this._relocateEvent('click', $event)
            }
        },

        /**
         * Event that fires on pressing enter on the control.
         * @param {Object} $event The event data.
         */
        onEnter($event) {
            if (!this.keyupEnterStarted) {
                this.keyupEnterStarted = true
                this._relocateEvent('keyup.enter', $event)
            }
        },

        /**
         * Event that fires on pressing space on the control.
         * @param {Object} $event The event data.
         */
        onSpace($event) {
            if (!this.keyupSpaceStarted) {
                this.keyupSpaceStarted = true
                this._relocateEvent('keyup.space', $event)
            }
        },
    },
}

</script>


<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.

.form {

    @include element('label') {
        @include user-select-none
    }
}

</style>
